import { useCallback, useMemo, useState } from "react";
import { useSignatureController } from "@/components/signiture/use-signature-controller";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useGetSignatureByProviderID } from "@/queries/signature/use-get-signature-by-providerID";
import { ProviderSignature } from "@/services";
import { getXtenantId } from "@/utils/utils";

// The signature endpoint returns one ProviderSignature, not the page its query hook is typed with.
const readStoredSignature = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const { providerSignature } = data as ProviderSignature;
  return providerSignature || undefined;
};

export interface UseCosignSignatureResult {
  canvasRef: ReturnType<typeof useSignatureController>["canvasRef"];
  signPad: ReturnType<typeof useSignatureController>["signPad"];
  canImportSignature: boolean;
  isSignEmpty: () => boolean;
  toDataUrl: () => string | undefined;
  clear: () => void;
  importStored: () => void;
}

/** Signature pad plus the logged-in provider's stored signature, as on the Sign And Lock drawer. */
export const useCosignSignature = (
  isOpen: boolean
): UseCosignSignatureResult => {
  // Flipping this key re-creates the pad after a clear, as the Sign And Lock drawer does.
  const [padKey, setPadKey] = useState(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    isOpen && padKey
  );
  const [me] = useCurrentUser();
  const { data: signatureData } = useGetSignatureByProviderID({
    providerUuid: me?.uuid ?? "",
    xTENANTID: getXtenantId(),
  });
  const storedSignature = readStoredSignature(signatureData?.data);

  const clear = useCallback(() => {
    signPad?.clear();
    setPadKey((prev) => !prev);
  }, [signPad]);
  const importStored = useCallback(() => {
    if (storedSignature) signPad?.fromDataURL(storedSignature);
  }, [signPad, storedSignature]);
  const toDataUrl = useCallback(() => signPad?.toDataURL(), [signPad]);

  return useMemo(
    () => ({
      canvasRef,
      signPad,
      canImportSignature: !!storedSignature,
      isSignEmpty,
      toDataUrl,
      clear,
      importStored,
    }),
    [
      canvasRef,
      signPad,
      storedSignature,
      isSignEmpty,
      toDataUrl,
      clear,
      importStored,
    ]
  );
};
